import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import {
  WriteProjectStorePeriod,
  ReadProjectStorePeriod,
} from "@/types/projectStorePeriod";
import { ProjectStorePeriodsService } from "@/modules/projects/service/projectStorePeriod";
import { ProjectStoreService } from "../service/projectStore";
import { ApiError, SuccessFetchListResponse } from "@/services/api";
import { Module } from "@/constants";
import { WriteProjectStore } from "@/types/projectStore";
import { Row } from "@tanstack/react-table";
import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import useQueryData from "@/hooks/useQueryData";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import usePeriodFilter from "../hooks/usePeriodFilter";
import { PeriodFilter } from "@/modules/projects/types/periodFilter";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { ProjectStoreFormStoreProvider } from "../stores/ProjectStoreFormContext";
import { useProjectStoreFormStoreApi } from "../hooks/useProjectStoreFormStoreApi";

interface ProjectStoreContextType {
  projectId: string;
  data: ReadProjectStorePeriod[];
  handleSubmit: () => Promise<void>;
  clearAndRemoveSingleStore: (row: Row<ReadProjectStorePeriod>) => void;
  clearAndRemoveSelectedStores: () => void;
  updatePeriodsInSelectedStores: (shouldBeActive: boolean) => void;
  isAddLimitsDialogOpen: boolean;
  setIsAddLimitsDialogOpen: (value: boolean) => void;
  inFetching: boolean;
  periodFilter: PeriodFilter;
}

const ProjectStoreContext = createContext<ProjectStoreContextType | undefined>(
  undefined,
);

export const ProjectStoreProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <ProjectStoreFormStoreProvider>
      <ProjectStoreProviderInner>{children}</ProjectStoreProviderInner>
    </ProjectStoreFormStoreProvider>
  );
};

const ProjectStoreProviderInner = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const formStoreApi = useProjectStoreFormStoreApi();

  const [isAddLimitsDialogOpen, setIsAddLimitsDialogOpen] = useState(false);
  const { t } = useTranslation();
  const { query } =
    useDataTableQuery<SuccessFetchListResponse<ReadProjectStorePeriod>>();
  const projectId = useProjectId();
  const data = useQueryData(query);

  const periodFilter = usePeriodFilter(data);
  const { store: rowSelectionStore } = useRowSelectionContext();

  useEffect(() => {
    if (query.isSuccess) {
      formStoreApi.getState().syncFromServer(data);
    }
  }, [query.isSuccess, data, formStoreApi]);

  const projectMutation = useMutationWithErrorHandling<
    WriteProjectStorePeriod,
    ApiError,
    WriteProjectStorePeriod,
    unknown
  >({
    mutationFn: async (values: WriteProjectStorePeriod) => {
      await ProjectStorePeriodsService.patch(projectId, values);
      return values;
    },
    errorTitle: t("projectStores.errors.updateProject"),
    onSuccess: () => {
      query.refetch();
    },
  });

  const removeStoresMutation = useMutationWithErrorHandling<
    WriteProjectStore,
    ApiError,
    WriteProjectStore,
    unknown
  >({
    mutationFn: async (values: WriteProjectStore) => {
      await ProjectStoreService.patch(projectId, values, Module.Stores);
      return values;
    },
    errorTitle: t("projectStores.errors.updateProject"),
    onSuccess: () => {
      query.refetch();
    },
  });

  const periodsIds = useMemo(() => {
    if (data.length === 0) return [];
    return data[0].periods
      .filter(
        (p) =>
          p.period >= periodFilter.fromDate && p.period <= periodFilter.toDate,
      )
      .sort((a, b) => a.period.localeCompare(b.period))
      .map((period) => period.id);
  }, [data, periodFilter.fromDate, periodFilter.toDate]);

  const handleSubmit = useCallback(async () => {
    formStoreApi.getState().setIsSubmitting(true);

    try {
      const changes = formStoreApi.getState().getChanges();

      await projectMutation.mutateAsync({
        stores_in_periods: changes.storesInPeriods,
        client_store_numbers: changes.clientStoreNumbers,
      });

      if (formStoreApi.getState().removeStoresWithoutPeriods) {
        const storesWithoutPeriods = formStoreApi
          .getState()
          .getStoresWithoutPeriods();
        if (storesWithoutPeriods.length > 0) {
          await removeStoresMutation.mutateAsync({
            add: [],
            delete: storesWithoutPeriods,
          });
        }
      }

      rowSelectionStore.clear();
    } finally {
      formStoreApi.getState().setIsSubmitting(false);
    }
  }, [formStoreApi, projectMutation, removeStoresMutation, rowSelectionStore]);

  const updatePeriodsInSelectedStores = useCallback(
    (shouldBeActive: boolean) => {
      const currentSelectedIds = Array.from(rowSelectionStore.getSelection());
      formStoreApi
        .getState()
        .bulkSetPeriods(currentSelectedIds, periodsIds, shouldBeActive);
    },
    [formStoreApi, periodsIds, rowSelectionStore],
  );

  const clearAndRemoveSingleStore = useCallback(
    (row: Row<ReadProjectStorePeriod>) => {
      formStoreApi.getState().clearAndRemoveStore(row.original.id, periodsIds);
    },
    [formStoreApi, periodsIds],
  );

  const clearAndRemoveSelectedStores = useCallback(() => {
    updatePeriodsInSelectedStores(false);
    formStoreApi.getState().setRemoveStoresWithoutPeriods(true);
  }, [updatePeriodsInSelectedStores, formStoreApi]);

  const inFetching =
    query.isFetching ||
    projectMutation.isPending ||
    removeStoresMutation.isPending;

  const value: ProjectStoreContextType = useMemo(
    () => ({
      projectId,
      data,
      handleSubmit,
      clearAndRemoveSingleStore,
      clearAndRemoveSelectedStores,
      updatePeriodsInSelectedStores,
      isAddLimitsDialogOpen,
      setIsAddLimitsDialogOpen,
      inFetching,
      periodFilter,
    }),
    [
      projectId,
      data,
      handleSubmit,
      clearAndRemoveSingleStore,
      clearAndRemoveSelectedStores,
      updatePeriodsInSelectedStores,
      isAddLimitsDialogOpen,
      setIsAddLimitsDialogOpen,
      inFetching,
      periodFilter,
    ],
  );

  return (
    <ProjectStoreContext.Provider value={value}>
      {children}
    </ProjectStoreContext.Provider>
  );
};

export const useProjectStoreContext = () => {
  const context = useContext(ProjectStoreContext);
  if (!context) {
    throw new Error(
      "useProjectStoreContext must be used within a ProjectStoreProvider",
    );
  }
  return context;
};
